import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Clock, BarChart3, X, Check, Globe, Building2, Users, ChevronRight, HelpCircle } from 'lucide-react';
import { LoadingSpinner, MultiSelect } from '../../../../common';
import { AdminPageHeader, AdminEmptyState, WorkspaceFilter, Pagination } from '../../../ui';
import * as agentService from '../../../../../services/agentService';
import * as workspaceService from '../../../../../services/workspaceService';
import { Agent, AgentCreate, Tag } from '../../../../../types/agent';
import { WorkspaceGroup } from '../../../../../types/workspace';
import { PaginationMetadata } from '../../../../../types/user';
import { DEFAULT_MODEL } from '../../../../../config/models';
import { useWorkspace } from '../../../../../contexts/WorkspaceContext';

// Agent Row Component
interface AgentRowProps {
  agent: Agent;
  onClick: () => void;
  isGlobalAgentReadOnly?: boolean;
}

const AgentRow: React.FC<AgentRowProps> = React.memo(({ agent, onClick, isGlobalAgentReadOnly = false }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // Get visibility icon and label
  const getVisibilityInfo = () => {
    if (!agent.visibility_level || agent.visibility_level === 'global') {
      return { icon: Globe, label: 'Global', color: 'text-purple-400', bgColor: 'bg-purple-500/20' };
    } else if (agent.visibility_level === 'workspace') {
      return { icon: Building2, label: 'Workspace', color: 'text-blue-400', bgColor: 'bg-blue-500/20' };
    } else {
      return { icon: Users, label: 'Group', color: 'text-green-400', bgColor: 'bg-green-500/20' };
    }
  };

  const visibilityInfo = getVisibilityInfo();
  const VisibilityIcon = visibilityInfo.icon;

  // Determine if this row should be clickable
  const isGlobal = agent.visibility_level === 'global';
  const isClickable = !isGlobal || !isGlobalAgentReadOnly;

  return (
    <tr
      onClick={isClickable ? onClick : undefined}
      className={`
        border-b border-[#333333]
        ${isClickable ? 'hover:bg-[#2a2a2a] cursor-pointer' : 'cursor-not-allowed opacity-60'}
        transition-colors
      `}
    >
      {/* Nome do Agente */}
      <td className="px-4 py-4">
        <div className="flex flex-col">
          <span className="text-white font-medium">{agent.name}</span>
          <span className="text-[#888888] text-sm line-clamp-1 mt-0.5">
            {agent.description || 'Sem descrição'}
          </span>
        </div>
      </td>

      {/* Visibilidade */}
      <td className="px-4 py-4">
        <span
          className={`
            inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full
            ${visibilityInfo.bgColor} ${visibilityInfo.color}
          `}
        >
          <VisibilityIcon className="w-3.5 h-3.5" />
          {visibilityInfo.label}
        </span>
      </td>

      {/* Status */}
      <td className="px-4 py-4">
        <span
          className={`
            inline-flex px-2.5 py-1 text-xs font-medium rounded-full
            ${agent.deploy_status
              ? 'bg-green-500/20 text-green-400'
              : 'bg-[#333333] text-[#888888]'
            }
          `}
        >
          {agent.deploy_status ? 'Deployed' : 'Draft'}
        </span>
      </td>

      {/* Tags */}
      <td className="px-4 py-4">
        {agent.tags && agent.tags.length > 0 ? (
          <div className="flex items-center gap-1">
            <span
              className="px-2 py-0.5 text-xs bg-[#F4CC04]/20 text-[#F4CC04] rounded-full whitespace-nowrap"
              title={agent.tags.map(t => t.name).join(', ')}
            >
              {agent.tags[0].name}
            </span>
            {agent.tags.length > 1 && (
              <span
                className="text-xs text-[#888888] font-medium"
                title={agent.tags.slice(1).map(t => t.name).join(', ')}
              >
                +{agent.tags.length - 1}
              </span>
            )}
          </div>
        ) : (
          <span className="text-xs text-[#666666]">—</span>
        )}
      </td>

      {/* Criado Por */}
      <td className="px-4 py-4">
        {agent.created_by_user_name ? (
          <div className="flex flex-col">
            <span className="text-sm text-white">{agent.created_by_user_name}</span>
            {agent.created_by_user_email && (
              <span className="text-xs text-[#888888]">{agent.created_by_user_email}</span>
            )}
          </div>
        ) : (
          <span className="text-xs text-[#666666]">—</span>
        )}
      </td>

      {/* Data de Criação */}
      <td className="px-4 py-4 text-sm text-[#888888] whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <Clock className="w-3.5 h-3.5" />
          {formatDate(agent.created_at)}
        </div>
      </td>

      {/* Data de Atualização */}
      <td className="px-4 py-4 text-sm text-[#888888] whitespace-nowrap">
        <div className="flex items-center gap-1.5">
          <BarChart3 className="w-3.5 h-3.5" />
          {formatDate(agent.updated_at)}
        </div>
      </td>

      {/* Ação */}
      <td className="px-4 py-4">
        <button
          onClick={isClickable ? (e) => {
            e.stopPropagation();
            onClick();
          } : undefined}
          disabled={!isClickable}
          className={`
            p-2 rounded-lg transition-colors
            ${isClickable
              ? 'text-[#F4CC04] hover:bg-[#F4CC04]/10 cursor-pointer'
              : 'text-[#666666] cursor-not-allowed'
            }
          `}
          title={isClickable ? "Ver detalhes" : "Apenas TD pode editar agentes globais"}
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </td>
    </tr>
  );
});

// Create Modal Component
// Retrieval Method Info Tooltip Component
const RetrievalMethodTooltip: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div className="relative inline-block ml-1">
      <button
        type="button"
        onMouseEnter={() => setIsVisible(true)}
        onMouseLeave={() => setIsVisible(false)}
        onClick={() => setIsVisible(!isVisible)}
        className="text-[#888888] hover:text-[#F4CC04] transition-colors"
      >
        <HelpCircle className="w-4 h-4" />
      </button>

      {isVisible && (
        <div className="absolute left-1/2 -translate-x-1/2 top-6 z-50 w-80 bg-[#1a1a1a] border border-[#333333] rounded-lg p-4 shadow-xl">
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-semibold text-[#F4CC04] mb-1">📄 Contexto Direto</p>
              <p className="text-[#cccccc] text-xs">
                Usado para bases de conhecimento com poucos documentos. O conteúdo é enviado diretamente no prompt do agente.
              </p>
            </div>

            <div className="border-t border-[#333333] pt-2">
              <p className="font-semibold text-[#F4CC04] mb-1">🗄️ Relacional</p>
              <p className="text-[#cccccc] text-xs">
                Busca dados em bancos de dados relacionais (SQL) usando perfis e filtros dinâmicos. Ideal para consultas estruturadas em tabelas.
              </p>
            </div>

            <div className="border-t border-[#333333] pt-2">
              <p className="font-semibold text-[#F4CC04] mb-1">🔍 Semântico Vetorial</p>
              <p className="text-[#cccccc] text-xs">
                Usa conceito de RAG (Retrieval-Augmented Generation) para bases de conhecimento maiores. Busca por similaridade em documentos vetorizados.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

interface CreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  data: AgentCreate;
  onChange: (data: AgentCreate) => void;
  workspaces: { id: number; name: string }[];
  groups: (WorkspaceGroup & { workspace_name?: string })[];
  currentWorkspaceId?: number;
  isTDWorkspace?: boolean;  // Flag to show/hide global visibility option
}

const CreateModal: React.FC<CreateModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  data,
  onChange,
  workspaces,
  groups,
  currentWorkspaceId,
  isTDWorkspace = false,
}) => {
  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={onClose}
      />
      <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md bg-[#2a2a2a] rounded-2xl p-6 z-50 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6 sticky top-0 bg-[#2a2a2a] pb-4 z-10">
          <h2 className="text-xl font-semibold text-white">Criar Agente</h2>
          <button
            onClick={onClose}
            className="p-2 text-[#888888] hover:text-white hover:bg-[#333333] rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Nome *
            </label>
            <input
              type="text"
              placeholder="Nome do agente"
              value={data.name}
              onChange={(e) => onChange({ ...data, name: e.target.value })}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
              "
              required
            />
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-[#cccccc] mb-2">
              Tipo de Recuperação *
              <RetrievalMethodTooltip />
            </label>
            <select
              value={data.retrieval_method}
              onChange={(e) => onChange({ ...data, retrieval_method: e.target.value })}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
                cursor-pointer
              "
              required
            >
              <option value="">Selecione o tipo</option>
              <option value="direct_context">📄 Contexto Direto</option>
              <option value="relational">🗄️ Relacional</option>
              <option value="semantic_vector">🔍 Semântico Vetorial</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Descrição
            </label>
            <textarea
              placeholder="Descrição do agente"
              value={data.description}
              onChange={(e) => onChange({ ...data, description: e.target.value })}
              rows={3}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white placeholder-[#888888]
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
                resize-none
              "
            />
          </div>

          {/* Visibility Settings */}
          <div className="border-t border-[#333333] pt-4">
            <label className="block text-sm font-medium text-[#cccccc] mb-2">
              Visibilidade
            </label>
            <select
              value={
                // SECURITY: Non-TD users cannot select 'global', force 'workspace' if they somehow have 'global'
                !isTDWorkspace && data.visibility_level === 'global'
                  ? 'workspace'
                  : data.visibility_level || (isTDWorkspace ? 'global' : 'workspace')
              }
              onChange={(e) => {
                const visibility_level = e.target.value as 'global' | 'workspace' | 'group';

                // SECURITY: Non-TD users cannot create global agents
                if (!isTDWorkspace && visibility_level === 'global') {
                  return;
                }

                const newData = {
                  ...data,
                  visibility_level,
                  workspace_id: visibility_level === 'global' ? null : (data.workspace_id || currentWorkspaceId),
                  workspace_ids: (visibility_level === 'workspace' || visibility_level === 'group') ? (data.workspace_ids || []) : [],
                  group_id: visibility_level !== 'group' ? null : data.group_id,
                  group_ids: visibility_level === 'group' ? (data.group_ids || []) : []
                };

                onChange(newData);
              }}
              className="
                w-full px-4 py-3
                bg-[#1a1a1a] border border-[#333333]
                text-white
                rounded-lg
                focus:outline-none focus:border-[#F4CC04]
                cursor-pointer
              "
            >
              {/* Only show "Global" option for TD workspace users */}
              {isTDWorkspace && (
                <option value="global">Global - Todos os usuários</option>
              )}
              <option value="workspace">Workspace - Usuários do workspace</option>
              <option value="group">Grupo (Compartilhamento granular por workspaces e grupos)</option>
            </select>
            <p className="mt-1 text-xs text-[#888888]">
              {data.visibility_level === 'global' && 'Agente visível para todos os usuários da plataforma'}
              {data.visibility_level === 'workspace' && 'Selecione os workspaces onde este agente estará disponível'}
              {data.visibility_level === 'group' && 'Selecione primeiro os workspaces, depois os grupos específicos que poderão ver este agente'}
            </p>
          </div>

          {/* Workspace Multi-Selection */}
          {(data.visibility_level === 'workspace' || data.visibility_level === 'group') && (
            <MultiSelect
              label="Workspaces"
              options={workspaces.map(w => ({ id: w.id, name: w.name }))}
              selectedIds={data.workspace_ids || []}
              onChange={(ids) => onChange({ ...data, workspace_ids: ids })}
              placeholder="Selecione os workspaces..."
              required
            />
          )}

          {/* Group Multi-Selection */}
          {data.visibility_level === 'group' && (
            <MultiSelect
              label="Grupos"
              options={groups
                .filter((g): g is WorkspaceGroup & { workspace_name?: string } => !g.deleted)
                .map(g => ({
                  id: g.id,
                  name: g.workspace_name ? `${g.name} - ${g.workspace_name}` : g.name
                }))
              }
              selectedIds={data.group_ids || []}
              onChange={(ids) => onChange({ ...data, group_ids: ids })}
              placeholder="Selecione os grupos..."
              required
            />
          )}

          <button
            type="submit"
            disabled={
              !data.name.trim() ||
              !data.retrieval_method ||
              ((data.visibility_level === 'workspace' || data.visibility_level === 'group') &&
                (!data.workspace_ids || data.workspace_ids.length === 0)) ||
              (data.visibility_level === 'group' &&
                (!data.group_ids || data.group_ids.length === 0))
            }
            className="
              w-full px-4 py-3 mt-2
              bg-[#F4CC04] hover:bg-[#D4B104]
              text-black font-medium
              rounded-lg transition-colors
              disabled:opacity-50 disabled:cursor-not-allowed
            "
          >
            Criar Agente
          </button>
        </form>
      </div>
    </>
  );
};

// Workspace data structure for pagination
interface WorkspaceAgentsData {
  agents: Agent[];
  currentPage: number;
  paginationData: PaginationMetadata | null;
  isLoading: boolean;
}

// Main Component
export const AgentManagement: React.FC = () => {
  const navigate = useNavigate();
  const { workspaces, currentWorkspace } = useWorkspace();
  const [tags, setTags] = useState<Tag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<number | null | 'global'>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [isLoadingTags, setIsLoadingTags] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [allGroups, setAllGroups] = useState<(WorkspaceGroup & { workspace_name?: string })[]>([]);
  const [allWorkspaces, setAllWorkspaces] = useState<{ id: number; name: string }[]>([]);

  // Per-workspace pagination state
  // Key: unique category key (e.g., "global" for global, "ws-{id}" for workspaces)
  const [workspaceData, setWorkspaceData] = useState<Record<string, WorkspaceAgentsData>>({});

  // Refs for cleanup and preventing memory leaks
  const isMountedRef = useRef(true);
  const abortControllersRef = useRef<Record<string, AbortController>>({});

  const [newAgentData, setNewAgentData] = useState<AgentCreate>({
    name: '',
    description: '',
    use_guided_prompt: true,
    use_security_prompt: true,
    model: DEFAULT_MODEL,
    temperature: 0.5,
    retrieval_method: '',
    visibility_level: 'workspace', // Default to workspace (will be reset when modal opens)
    workspace_id: currentWorkspace?.id || null,
    group_id: null,
    workspace_ids: [],
    group_ids: []
  });

  // Page size for pagination
  const pageLimit = 20;

  // Helper function to get initial agent data based on current workspace
  const getInitialAgentData = useCallback((): AgentCreate => ({
    name: '',
    description: '',
    use_guided_prompt: true,
    use_security_prompt: true,
    model: DEFAULT_MODEL,
    temperature: 0.5,
    retrieval_method: '',
    visibility_level: currentWorkspace?.is_td_workspace ? 'global' : 'workspace',
    workspace_id: currentWorkspace?.is_td_workspace ? null : (currentWorkspace?.id || null),
    group_id: null,
    workspace_ids: [],
    group_ids: []
  }), [currentWorkspace]);

  // Open create modal with fresh state
  const handleOpenCreateModal = useCallback(() => {
    const initialData = getInitialAgentData();
    setNewAgentData(initialData);
    setShowCreateModal(true);
  }, [getInitialAgentData]);

  // SECURITY FIX: Ensure non-TD users never have 'global' visibility in newAgentData
  // This acts as a defense-in-depth layer to prevent accidental 'global' creation
  useEffect(() => {
    if (!currentWorkspace?.is_td_workspace && newAgentData.visibility_level === 'global') {
      setNewAgentData(prev => ({
        ...prev,
        visibility_level: 'workspace',
        workspace_id: currentWorkspace?.id || prev.workspace_id
      }));
    }
  }, [newAgentData.visibility_level, currentWorkspace]);

  // Handle workspace change
  const handleWorkspaceChange = useCallback((workspaceId: number | null | 'global') => {
    setWorkspaceData({});
    setSelectedWorkspaceId(workspaceId);
  }, []);

  // Fetch agents for a specific category
  // NOTE: This implementation prevents race conditions and memory leaks by:
  // 1. Aborting previous requests when new ones start (prevents processing stale data)
  // 2. Checking if component is mounted before setState (prevents memory leaks)
  // 3. Cleaning up all controllers on unmount
  // FUTURE IMPROVEMENT: Pass AbortSignal to API service to cancel HTTP requests mid-flight
  const fetchCategoryAgents = useCallback(
    async (categoryKey: string, page: number = 1, search: string = '', tagIds: number[] = []) => {
      try {
        // Cancel any existing request for this category
        if (abortControllersRef.current[categoryKey]) {
          abortControllersRef.current[categoryKey].abort();
        }

        // Create new AbortController for this request
        const abortController = new AbortController();
        abortControllersRef.current[categoryKey] = abortController;

        // Update loading state for this category
        if (!isMountedRef.current) return;
        setWorkspaceData(prev => ({
          ...prev,
          [categoryKey]: {
            agents: prev[categoryKey]?.agents || [],
            currentPage: prev[categoryKey]?.currentPage || page,
            paginationData: prev[categoryKey]?.paginationData || null,
            isLoading: true
          }
        }));

        setError(null);

        // Determine workspace filter and visibility level based on category
        let workspaceFilter: number | undefined = undefined;
        let visibilityLevelFilter: string | undefined = undefined;

        if (categoryKey === 'global') {
          // Global category - show only agents with visibility_level='global'
          workspaceFilter = undefined;
          visibilityLevelFilter = 'global';
        } else if (categoryKey.startsWith('ws-')) {
          // Extract workspace ID from key format "ws-{id}"
          const wsId = parseInt(categoryKey.substring(3));
          if (!isNaN(wsId)) {
            workspaceFilter = wsId;
          }
        }

        const response = await agentService.listAgents(
          page,
          pageLimit,
          search,
          false,
          tagIds.length > 0 ? tagIds : undefined,
          undefined,
          workspaceFilter,
          visibilityLevelFilter
        );

        // Check if component was unmounted - if so, don't update state
        if (!isMountedRef.current) {
          delete abortControllersRef.current[categoryKey];
          return;
        }

        // Clean up this controller
        delete abortControllersRef.current[categoryKey];

        if (response.success) {
          // Backend already filters by visibility_level, no need for frontend filtering
          const filteredAgents = response.data;

          setWorkspaceData(prev => ({
            ...prev,
            [categoryKey]: {
              agents: filteredAgents,
              currentPage: page,
              paginationData: response.metadata,
              isLoading: false
            }
          }));
        } else {
          setError(response.message || 'Falha ao carregar agentes');
          setWorkspaceData(prev => ({
            ...prev,
            [categoryKey]: {
              agents: prev[categoryKey]?.agents || [],
              currentPage: prev[categoryKey]?.currentPage || page,
              paginationData: prev[categoryKey]?.paginationData || null,
              isLoading: false
            }
          }));
        }
      } catch (err) {
        // Ignore abort errors but still clean up loading state
        if (err instanceof Error && err.name === 'AbortError') {
          if (isMountedRef.current) {
            setWorkspaceData(prev => ({
              ...prev,
              [categoryKey]: {
                ...prev[categoryKey],
                agents: prev[categoryKey]?.agents || [],
                currentPage: prev[categoryKey]?.currentPage || page,
                paginationData: prev[categoryKey]?.paginationData || null,
                isLoading: false,
              }
            }));
          }
          delete abortControllersRef.current[categoryKey];
          return;
        }

        // Component unmounted, clean up loading state before returning
        if (!isMountedRef.current) {
          delete abortControllersRef.current[categoryKey];
          return;
        }

        setError(err instanceof Error ? err.message : 'Falha ao carregar agentes');
        setWorkspaceData(prev => ({
          ...prev,
          [categoryKey]: {
            agents: prev[categoryKey]?.agents || [],
            currentPage: prev[categoryKey]?.currentPage || page,
            paginationData: prev[categoryKey]?.paginationData || null,
            isLoading: false
          }
        }));

        // Clean up this controller
        delete abortControllersRef.current[categoryKey];
      }
    },
    [pageLimit]
  );

  const fetchTags = useCallback(async () => {
    try {
      setIsLoadingTags(true);
      const tags = await agentService.getAllTags();
      setTags(tags);
    } catch (err) {
      console.error('Error fetching tags:', err);
    } finally {
      setIsLoadingTags(false);
    }
  }, []);

  // Fetch all workspaces on mount
  useEffect(() => {
    const fetchAllWorkspaces = async () => {
      try {
        const response = await workspaceService.getUserWorkspaces();
        if (response.success && response.data) {
          setAllWorkspaces(response.data);
        }
      } catch (err) {
        console.error('Failed to fetch workspaces:', err);
      }
    };
    fetchAllWorkspaces();
  }, []);

  // Fetch groups from ALL selected workspaces for create modal
  useEffect(() => {
    const fetchGroupsFromAllWorkspaces = async () => {
      const selectedWorkspaceIds = newAgentData.workspace_ids;
      if (!selectedWorkspaceIds || selectedWorkspaceIds.length === 0) {
        setAllGroups([]);
        return;
      }

      try {
        const groupsPromises = selectedWorkspaceIds.map(async (wsId) => {
          const response = await workspaceService.getWorkspaceGroups(wsId);
          if (response.success && response.data) {
            const workspace = allWorkspaces.find(w => w.id === wsId);
            return response.data.map(group => ({
              ...group,
              workspace_name: workspace?.name || `Workspace ${wsId}`
            }));
          }
          return [];
        });

        const allGroupsArrays = await Promise.all(groupsPromises);
        const allGroups = allGroupsArrays.flat();
        setAllGroups(allGroups);
      } catch (err) {
        console.error('Failed to fetch groups from workspaces:', err);
        setAllGroups([]);
      }
    };

    fetchGroupsFromAllWorkspaces();
  }, [newAgentData.workspace_ids, allWorkspaces]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    if (showCreateModal) {
      setNewAgentData({
        name: '',
        description: '',
        use_guided_prompt: true,
        use_security_prompt: true,
        model: DEFAULT_MODEL,
        temperature: 0.5,
        retrieval_method: '',
        visibility_level: 'workspace',
        workspace_id: currentWorkspace?.id || null,
        group_id: null,
        workspace_ids: [],
        group_ids: []
      });
    }
  }, [showCreateModal, currentWorkspace?.id]);

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Get category info with unique keys and display names
  const categories = useMemo(() => {
    const cats: Array<{ key: string; name: string; workspaceId?: number }> = [];

    if (selectedWorkspaceId === null) {
      // Show all workspaces
      cats.push({ key: 'global', name: 'Agentes Globais (TD)' });
      workspaces.forEach(w => {
        cats.push({ key: `ws-${w.id}`, name: w.name, workspaceId: w.id });
      });
    } else if (selectedWorkspaceId === 'global') {
      // Show only global
      cats.push({ key: 'global', name: 'Agentes Globais (TD)' });
    } else {
      // Show specific workspace
      const workspace = workspaces.find(w => w.id === selectedWorkspaceId);
      if (workspace) {
        cats.push({ key: `ws-${workspace.id}`, name: workspace.name, workspaceId: workspace.id });
      }
    }

    return cats;
  }, [selectedWorkspaceId, workspaces]);

  // Fetch initial data when selectedWorkspaceId or search changes
  useEffect(() => {
    categories.forEach(cat => {
      fetchCategoryAgents(cat.key, 1, debouncedSearchQuery, selectedTagIds);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspaceId, debouncedSearchQuery, selectedTagIds, workspaces]);

  // Cleanup on unmount - abort all pending requests
  useEffect(() => {
    // Set mounted to true on mount (important for React.StrictMode remounts)
    isMountedRef.current = true;

    return () => {
      isMountedRef.current = false;
      // Abort all pending requests
      Object.values(abortControllersRef.current).forEach(controller => {
        controller.abort();
      });
      abortControllersRef.current = {};
    };
  }, []);

  const handleCreateAgent = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setError(null);
      const agentData = { ...newAgentData };

      if (!agentData.retrieval_method) {
        delete agentData.retrieval_method;
      }

      const response = await agentService.createAgent(agentData);
      if (response.success) {
        setShowCreateModal(false);

        // Refresh all categories
        categories.forEach(cat => {
          const data = workspaceData[cat.key];
          if (data) {
            fetchCategoryAgents(cat.key, data.currentPage, debouncedSearchQuery, selectedTagIds);
          } else {
            fetchCategoryAgents(cat.key, 1, debouncedSearchQuery, selectedTagIds);
          }
        });

        // Reset form to initial state
        setNewAgentData(getInitialAgentData());
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Falha ao criar agente');
    }
  };

  const handleTagClick = useCallback((tagId: number) => {
    setSelectedTagIds((prev) =>
      prev.includes(tagId) ? prev.filter((id) => id !== tagId) : [...prev, tagId]
    );
  }, []);

  const clearTagFilters = useCallback(() => {
    setSelectedTagIds([]);
  }, []);

  const handleAgentClick = useCallback((agentId: number) => {
    navigate(`/admin/agents/${agentId}`);
  }, [navigate]);

  // Handle page change for specific category
  const handleCategoryPageChange = useCallback((category: string, page: number) => {
    fetchCategoryAgents(category, page, debouncedSearchQuery, selectedTagIds);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchCategoryAgents, debouncedSearchQuery, selectedTagIds]);

  // Check if there are any agents across all categories
  const hasAnyAgents = Object.values(workspaceData).some(data => data?.agents && data.agents.length > 0);

  // Check if any category is currently loading
  const isAnyLoading = Object.values(workspaceData).some(data => data?.isLoading);

  // Show initial loading if still loading AND no data has arrived yet
  const showInitialLoading = isAnyLoading && !hasAnyAgents;

  return (
    <div className="w-full max-w-7xl mx-auto px-4 md:px-6 py-6">
      <AdminPageHeader
        title="Agentes"
        subtitle="Gerencie os agentes de conversação"
        actions={
          <button
            onClick={handleOpenCreateModal}
            className="
              flex items-center gap-2 px-4 py-2
              bg-[#F4CC04] hover:bg-[#D4B104]
              text-black font-medium
              rounded-lg transition-colors
            "
          >
            <Plus className="w-5 h-5" />
            Novo Agente
          </button>
        }
      />

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {/* Search */}
      <div className="relative mb-4">
        <input
          type="text"
          placeholder="Pesquisar agentes..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="
            w-full px-4 py-3 pl-12
            bg-[#1a1a1a] border border-[#333333]
            text-white placeholder-[#888888]
            rounded-lg
            focus:outline-none focus:border-[#F4CC04]
          "
        />
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#888888]" />
      </div>

      {/* Workspace Filter */}
      <WorkspaceFilter
        workspaces={workspaces}
        selectedWorkspaceId={selectedWorkspaceId}
        onWorkspaceChange={handleWorkspaceChange}
        isLoading={false}
        className="mb-4"
      />

      {/* Tag Filters */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-medium text-[#888888]">Filtrar por tags</h3>
          {selectedTagIds.length > 0 && (
            <button
              onClick={clearTagFilters}
              className="text-sm text-[#F4CC04] hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {isLoadingTags ? (
            <LoadingSpinner size="small" />
          ) : tags.length > 0 ? (
            tags.map((tag) => (
              <button
                key={tag.id}
                onClick={() => handleTagClick(tag.id)}
                className={`
                  flex items-center gap-1 px-3 py-1.5 text-sm rounded-full
                  transition-colors
                  ${selectedTagIds.includes(tag.id)
                    ? 'bg-[#F4CC04] text-black'
                    : 'bg-[#333333] text-[#cccccc] hover:bg-[#444444]'
                  }
                `}
              >
                {tag.name}
                {selectedTagIds.includes(tag.id) && <Check className="w-3 h-3" />}
              </button>
            ))
          ) : (
            <span className="text-sm text-[#888888]">Nenhuma tag disponível</span>
          )}
        </div>
      </div>

      {/* Agents Table - By Workspace Category */}
      <div className="space-y-8">
        {showInitialLoading ? (
          <div className="flex items-center justify-center py-20">
            <LoadingSpinner size="large" />
          </div>
        ) : hasAnyAgents ? (
          <>
            {categories.map((cat) => {
              const categoryData = workspaceData[cat.key];

              // Skip if no data yet or still loading for the first time
              if (!categoryData) return null;

              // Skip if no agents in this category
              if (!categoryData.agents || categoryData.agents.length === 0) return null;

              return (
                <div key={cat.key} className="space-y-3">
                  {/* Category Header */}
                  <div className="flex items-center gap-3 pb-2 border-b border-[#333333]">
                    <h3 className="text-lg font-semibold text-white">{cat.name}</h3>
                    <span className="text-sm text-[#888888]">
                      ({categoryData.agents.length} {categoryData.agents.length === 1 ? 'agente' : 'agentes'})
                    </span>
                  </div>

                  {/* Loading state for this category */}
                  {categoryData.isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <LoadingSpinner size="medium" />
                    </div>
                  ) : (
                    <>
                      {/* Table for this Category */}
                      <div className="bg-[#1a1a1a] rounded-lg border border-[#333333] overflow-hidden">
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-[#333333] bg-[#2a2a2a]">
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Nome
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Visibilidade
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Status
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Tags
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Criado Por
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Criado em
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Atualizado em
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-[#888888] uppercase tracking-wider">
                                  Ação
                                </th>
                              </tr>
                            </thead>
                            <tbody>
                              {categoryData.agents.map((agent) => (
                                <AgentRow
                                  key={agent.id}
                                  agent={agent}
                                  onClick={() => handleAgentClick(agent.id)}
                                  isGlobalAgentReadOnly={!currentWorkspace?.is_td_workspace}
                                />
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>

                      {/* Pagination for this Category */}
                      {categoryData.paginationData && categoryData.paginationData.pages > 1 && (
                        <div className="flex justify-center mt-4">
                          <Pagination
                            currentPage={categoryData.currentPage}
                            totalPages={categoryData.paginationData.pages}
                            onPageChange={(page) => handleCategoryPageChange(cat.key, page)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
          </>
        ) : (
          <AdminEmptyState
            title={
              debouncedSearchQuery || selectedTagIds.length > 0
                ? 'Nenhum agente encontrado'
                : 'Nenhum agente ainda'
            }
            description={
              debouncedSearchQuery || selectedTagIds.length > 0
                ? 'Tente ajustar seus termos de pesquisa ou filtros de tags'
                : 'Crie seu primeiro agente para começar'
            }
            action={
              selectedTagIds.length > 0 ? (
                <button
                  onClick={clearTagFilters}
                  className="px-4 py-2 bg-[#333333] hover:bg-[#444444] text-white rounded-lg transition-colors"
                >
                  Limpar Filtros
                </button>
              ) : (
                <button
                  onClick={handleOpenCreateModal}
                  className="px-4 py-2 bg-[#F4CC04] hover:bg-[#D4B104] text-black font-medium rounded-lg transition-colors"
                >
                  Criar Agente
                </button>
              )
            }
          />
        )}
      </div>

      <CreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreateAgent}
        data={newAgentData}
        onChange={setNewAgentData}
        workspaces={allWorkspaces}
        groups={allGroups}
        currentWorkspaceId={currentWorkspace?.id}
        isTDWorkspace={currentWorkspace?.is_td_workspace || false}
      />
    </div>
  );
};

export default AgentManagement;
